import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Network, X, Trash2, Users } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function AdminCourseMappingsPage() {
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publishedCourses, setPublishedCourses] = useState([]);
  const [dlg, setDlg] = useState(null); // create
  const [form, setForm] = useState({ course_id: "", mandatory_or_optional: "optional", auto_assign_all_staff: false, access_start_date: "", access_end_date: "" });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // mapping row pending deletion
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [o, c] = await Promise.all([
          api.get("/admin/organizations"),
          api.get("/admin/courses", { params: { status: "published", page_size: 200 } }),
        ]);
        setOrgs(o.data.data.organizations);
        setPublishedCourses(c.data.data.courses);
        if (o.data.data.organizations.length > 0) setOrgId(o.data.data.organizations[0].id);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/organizations/${orgId}/course-mappings`);
      setRows(data.data.mappings);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.course_id) return toast.error("Select a course.");
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.access_start_date) delete body.access_start_date;
      if (!body.access_end_date) delete body.access_end_date;
      await api.post(`/admin/organizations/${orgId}/course-mappings`, body);
      toast.success("Course mapped.");
      setDlg(null); setForm({ course_id: "", mandatory_or_optional: "optional", auto_assign_all_staff: false, access_start_date: "", access_end_date: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (m) => {
    try {
      await api.patch(`/admin/organizations/${orgId}/course-mappings/${m.id}/status`, { status: m.status === "active" ? "inactive" : "active" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteMapping = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/organizations/${orgId}/course-mappings/${confirmDelete.id}`);
      toast.success("Course removed from organization.");
      setConfirmDelete(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDeleting(false); }
  };

  return (
    <DashboardLayout title="Course mappings" breadcrumbs={["Platform", "Courses", "Mappings"]}>
      <div className="space-y-4" data-testid="admin-course-mappings">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
          <div className="text-sm text-slate-600 mr-2">Map published courses to organizations. Hospital Admins can only see mapped courses.</div>
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger className="w-72" data-testid="admin-mapping-org-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
            <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.organization_name} ({o.organization_code})</SelectItem>)}</SelectContent>
          </Select>
          <div className="ml-auto">
            <button onClick={() => setDlg({})} disabled={!orgId} className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="admin-mapping-add">
              <Plus className="h-4 w-4" /> Map course
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white">
          {loading ? <LoadingBlock label="Loading mappings…" /> : rows.length === 0 ? (
            <EmptyState title="No mappings yet" description="Map a published course to this organization." icon={Network} />
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Course</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Mandatory</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Auto-assign</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Access window</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>
                      <div className="font-semibold text-slate-900">{m.course?.course_title || "—"}</div>
                      <div className="font-mono text-xs text-slate-500">{m.course?.course_code}</div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 capitalize">{m.course?.course_type?.replace(/_/g, " ")}</TableCell>
                    <TableCell>
                      {m.mandatory_or_optional === "mandatory" ? (
                        <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700">Mandatory</span>
                      ) : (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">Optional</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{m.auto_assign_all_staff ? "Yes" : "No"}</TableCell>
                    <TableCell className="text-xs text-slate-600">{m.access_start_date || "—"} → {m.access_end_date || "—"}</TableCell>
                    <TableCell><StatusBadge status={m.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-3">
                        <button
                          onClick={() => window.open(`/app/hospital/courses/${m.course_id}?org_id=${orgId}`, "_blank")}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#101876] hover:text-[#0c135c]"
                          title="Assign or remove staff for this course"
                          data-testid={`mapping-assign-${m.id}`}
                        >
                          <Users className="h-3.5 w-3.5" />
                          Assign / Remove
                        </button>
                        <button
                          onClick={() => toggleStatus(m)}
                          className="text-xs font-semibold text-slate-500 hover:text-[#101876]"
                          data-testid={`mapping-toggle-${m.id}`}
                        >
                          {m.status === "active" ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          onClick={() => setConfirmDelete(m)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:text-rose-700"
                          title="Remove course from this organization"
                          data-testid={`mapping-delete-${m.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Map course to organization</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Course *</Label>
              <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                <SelectTrigger data-testid="mapping-course-select"><SelectValue placeholder="Select published course" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {publishedCourses.map((c) => <SelectItem key={c.id} value={c.id}>{c.course_title} ({c.course_code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Access start</Label><Input type="date" value={form.access_start_date} onChange={(e) => setForm({ ...form, access_start_date: e.target.value })} /></div>
              <div className="space-y-1"><Label>Access end</Label><Input type="date" value={form.access_end_date} onChange={(e) => setForm({ ...form, access_end_date: e.target.value })} /></div>
            </div>
            <div className="space-y-1">
              <Label>Mandatory / Optional</Label>
              <Select value={form.mandatory_or_optional} onValueChange={(v) => setForm({ ...form, mandatory_or_optional: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="optional">Optional</SelectItem>
                  <SelectItem value="mandatory">Mandatory</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/60 p-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Auto-assign to all staff</div>
                <div className="text-xs text-slate-500">Immediately create assignments for every active staff in the org.</div>
              </div>
              <Switch checked={form.auto_assign_all_staff} onCheckedChange={(v) => setForm({ ...form, auto_assign_all_staff: v })} data-testid="mapping-auto-assign" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDlg(null)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="mapping-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Map course
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-md" data-testid="mapping-delete-dialog">
          <DialogHeader>
            <DialogTitle>Remove course from organization?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-slate-600">
            <p>
              <span className="font-semibold text-slate-900">{confirmDelete?.course?.course_title || "This course"}</span>
              {" "}will be removed from this organization&apos;s mapped courses list.
            </p>
            <p className="text-xs text-slate-500">
              Related department and designation mappings for this course in this organization will also be cleaned up. Existing learner progress on this course is preserved.
            </p>
          </div>
          <DialogFooter>
            <button
              onClick={() => setConfirmDelete(null)}
              className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="mapping-delete-cancel"
            >
              Cancel
            </button>
            <button
              onClick={deleteMapping}
              disabled={deleting}
              className="inline-flex items-center gap-2 rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-70"
              data-testid="mapping-delete-confirm"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete mapping
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
